function clampNumber(n, min, max) {
  return n < min ? min : n > max ? max : n;
}
function isNumber(v) {
  return typeof v === "number" && v === v;
}
function defaultOnProgress(node, progress) {
  node.style.opacity = (progress * 100 + 0.5 | 0) / 100;
}
function defaultOnCleanup(node) {
  node.style.opacity = "";
}
function CustomFade(userOptions = {}) {
  const renderProgress = userOptions.onProgress || defaultOnProgress;
  const renderCleanup = userOptions.onCleanup || defaultOnCleanup;
  const stacked = !!userOptions.alwaysStacked;
  const FULL = 1;
  const NONE = 0;
  const FRICTION = 0.68;
  let api;
  let engine;
  let nodes;
  let snapTransforms;
  let offTransform;
  let opacities;
  let fadeToNextDist;
  let distFromPointer = 0;
  let fadeVel = 0;
  let prog = 0;
  let pairActive = false;
  let origSettled;
  let origProgress;
  const ctx = { isIncoming: false, snapIndex: 0, slideIndex: 0, direction: 0 };
  function init(apiInstance) {
    api = apiInstance;
    engine = api.internalEngine();
    nodes = api.slideNodes();
    const selected = api.selectedScrollSnap();
    const size = engine.axis.measureSize(engine.containerRect);
    fadeToNextDist = clampNumber(size * 0.75, 200, 500);
    pairActive = false;
    opacities = engine.scrollSnaps.map((_, i) => i === selected ? FULL : NONE);
    const axU = engine.axis.scroll.toUpperCase();
    const dir = engine.axis.direction;
    snapTransforms = engine.scrollSnaps.map((s) => `translate${axU}(${dir(s)}px)`);
    if (!stacked) {
      offTransform = `translate${axU}(${dir(engine.containerRect.width + 2)}px)`;
    }
    origSettled = engine.scrollBody.settled;
    origProgress = api.scrollProgress;
    engine.scrollBody.settled = settled;
    api.scrollProgress = scrollProgress;
    api.on("select", onSelect).on("slideFocus", fadeInstant).on("pointerDown", onPointerDown).on("pointerUp", onPointerUp);
    disableScroll();
    fadeInstant();
  }
  function destroy() {
    engine.scrollBody.settled = origSettled;
    api.scrollProgress = origProgress;
    api.off("select", onSelect).off("slideFocus", fadeInstant).off("pointerDown", onPointerDown).off("pointerUp", onPointerUp);
    for (let i = 0, len = nodes.length; i < len; i++) {
      const s = nodes[i].style;
      s.transform = "";
      s.pointerEvents = "";
      renderCleanup(nodes[i]);
      if (!nodes[i].getAttribute("style")) nodes[i].removeAttribute("style");
    }
  }
  function fadeInstant() {
    updateOpacities(api.selectedScrollSnap(), FULL);
  }
  function onPointerUp() {
    pairActive = false;
  }
  function onPointerDown() {
    pairActive = false;
    distFromPointer = 0;
    fadeVel = 0;
  }
  function onSelect() {
    const dur = engine.scrollBody.duration();
    fadeVel = dur ? 0 : FULL;
    pairActive = true;
    if (!dur) fadeInstant();
  }
  function disableScroll() {
    engine.translate.clear();
    engine.translate.toggleActive(false);
    const pts = engine.slideLooper.loopPoints;
    for (let i = 0, len = pts.length; i < len; i++) {
      pts[i].translate.clear();
      pts[i].translate.toggleActive(false);
    }
  }
  function lockExcessiveScroll(fi) {
    if (!isNumber(fi) || opacities[fi] < 0.5) return;
    engine.location.set(engine.scrollSnaps[fi]);
    engine.target.set(engine.location);
  }
  function updateOpacities(fadeIdx, velocity) {
    const snaps = engine.scrollSnaps;
    const absV = velocity < 0 ? -velocity : velocity;
    const selected = api.selectedScrollSnap();
    const previous = api.previousScrollSnap();
    const len = snaps.length;
    const dir = selected > previous ? 1 : selected < previous ? -1 : 0;
    for (let i = 0; i < len; i++) {
      const isFade = i === fadeIdx;
      const next = isFade ? opacities[i] + absV : opacities[i] - absV;
      const clamped = next < NONE ? NONE : next > FULL ? FULL : next;
      opacities[i] = clamped;
      if (isFade && pairActive) opacities[previous] = 1 - clamped;
      if (isFade) calcProgress(fadeIdx, clamped);
      renderSlide(i, selected, dir);
    }
  }
  function renderSlide(index, selectedSnap, dir) {
    const reg = engine.slideRegistry[index];
    const val = opacities[index];
    const incoming = index === selectedSnap;
    if (stacked) {
      for (let i = 0, len = reg.length; i < len; i++) {
        const si = reg[i];
        const style = nodes[si].style;
        style.transform = snapTransforms[index];
        ctx.isIncoming = incoming;
        ctx.snapIndex = index;
        ctx.slideIndex = si;
        ctx.direction = dir;
        renderProgress(nodes[si], val, ctx);
        style.pointerEvents = val > 0.5 ? "auto" : "none";
      }
    } else {
      const visible = val > 5e-3;
      const tf = visible ? snapTransforms[index] : offTransform;
      for (let i = 0, len = reg.length; i < len; i++) {
        const si = reg[i];
        const style = nodes[si].style;
        if (visible) style.transform = tf;
        ctx.isIncoming = incoming;
        ctx.snapIndex = index;
        ctx.slideIndex = si;
        ctx.direction = dir;
        renderProgress(nodes[si], val, ctx);
        style.pointerEvents = val > 0.5 ? "auto" : "none";
        if (!visible) style.transform = tf;
      }
    }
  }
  function calcProgress(fadeIdx, opacity) {
    const snaps = engine.scrollSnaps;
    const pDown = engine.dragHandler.pointerDown();
    const frac = 1 / (snaps.length - 1);
    let a = fadeIdx;
    let b = pDown ? api.selectedScrollSnap() : api.previousScrollSnap();
    if (pDown && a === b) {
      const rev = distFromPointer > 0 ? -1 : distFromPointer < 0 ? 1 : 0;
      a = b;
      b = engine.index.clone().set(b).add(rev).get();
    }
    prog = b * frac + (a - b) * frac * opacity;
  }
  function getFadeIndex() {
    const selected = api.selectedScrollSnap();
    if (!engine.dragHandler.pointerDown()) return selected;
    const vel = engine.scrollBody.velocity();
    const dirS = vel > 0 ? 1 : vel < 0 ? -1 : 0;
    const distS = distFromPointer > 0 ? 1 : distFromPointer < 0 ? -1 : 0;
    if (!dirS || !distS) return null;
    const next = engine.index.clone().set(selected).add(-dirS).get();
    return distS === dirS ? next : selected;
  }
  function tick() {
    const pDown = engine.dragHandler.pointerDown();
    const vel = engine.scrollBody.velocity();
    const dur = engine.scrollBody.duration();
    const fi = getFadeIndex();
    if (pDown) {
      if (!vel) return;
      distFromPointer += vel;
      fadeVel = (vel < 0 ? -vel : vel) / fadeToNextDist;
      lockExcessiveScroll(fi);
    } else {
      if (!dur || !isNumber(fi)) return;
      fadeVel += (FULL - opacities[fi]) / dur;
      fadeVel *= FRICTION;
    }
    if (!isNumber(fi)) return;
    updateOpacities(fi, fadeVel);
  }
  function settled() {
    const diff = engine.target.get() - engine.location.get();
    const notReached = diff > 1 || diff < -1;
    const fi = getFadeIndex();
    tick();
    if (!isNumber(fi) || notReached) return false;
    return opacities[fi] > 0.999;
  }
  function scrollProgress() {
    return prog;
  }
  return { name: "customFade", options: userOptions, init, destroy };
}
CustomFade.globalOptions = void 0;
export {
  CustomFade as default
};
